import { useEffect, useState } from 'react'
import { X, Save, Upload, Loader2 } from 'lucide-react'
import { uploadImage } from '@/modules/uploads-api'
import type { FlashDeal } from '@/modules/promo/model/types'
import { useAdminProductsStore } from '../../../state/productsStore'
import { useAdminPromoStore } from '../../../state/promoStore'
import { useToast } from '../../components/Toast'
import { formatVND } from '@/lib/formatters'

interface DealFormDialogProps {
  open: boolean
  editing: FlashDeal | null
  onClose: () => void
}

const EMPTY: Omit<FlashDeal, 'id'> = {
  slug: '', name: '', image: '',
  oldPrice: 0, price: 0, sold: 0, stock: 50,
}

export function DealFormDialog({ open, editing, onClose }: DealFormDialogProps) {
  const products = useAdminProductsStore((s) => s.products)
  const add = useAdminPromoStore((s) => s.addDeal)
  const update = useAdminPromoStore((s) => s.updateDeal)
  const push = useToast((s) => s.push)

  const [v, setV] = useState<Omit<FlashDeal, 'id'>>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState(false)

  const doUpload = async (file?: File | null) => {
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadImage(file)
      upd('image', url)
    } catch (err: any) {
      // silent
    } finally {
      setUploading(false)
    }
  }

  const onPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        e.preventDefault()
        doUpload(it.getAsFile())
        return
      }
    }
  }

  useEffect(() => {
    if (!open) return
    setErrors({})
    setV(editing ? { ...editing } : EMPTY)
  }, [open, editing])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const upd = <K extends keyof typeof v>(k: K, val: typeof v[K]) => {
    setV((s) => ({ ...s, [k]: val }))
    setErrors((e) => ({ ...e, [k as string]: '' }))
  }

  const handlePickProduct = (slug: string) => {
    const p = products.find((x) => x.slug === slug)
    if (!p) return
    setV({
      slug: p.slug,
      name: p.name,
      image: p.image,
      oldPrice: p.oldPrice,
      price: p.price,
      sold: v.sold,
      stock: v.stock || 50,
    })
    setErrors({})
  }

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const handleSave = async () => {
    const errs: Record<string, string> = {}
    if (!v.slug.trim())  errs.slug  = 'Vui lòng chọn hoặc nhập slug'
    if (!v.name.trim())  errs.name  = 'Vui lòng nhập tên'
    if (!v.image.trim()) errs.image = 'Cần URL ảnh'
    if (v.price <= 0)    errs.price = 'Giá phải > 0'
    if (v.stock <= 0)    errs.stock = 'Tổng số phải > 0'
    if (v.sold > v.stock) errs.sold = 'Đã bán không thể vượt tổng số'
    if (Object.keys(errs).length) { setErrors(errs); return }

    try {
      if (editing) {
        await update(editing.id, v)
        push('success', `Đã cập nhật deal "${v.name}"`)
      } else {
        await add(v)
        push('success', `Đã thêm deal "${v.name}"`)
      }
      onClose()
    } catch (err: any) {
      console.error('[deal save]', err)
      push('error', err?.response?.data?.message ?? err?.message ?? 'Lưu thất bại')
    }
  }

  const discountPct = v.oldPrice > v.price && v.oldPrice > 0
    ? Math.round(((v.oldPrice - v.price) / v.oldPrice) * 100)
    : 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div className="relative bg-surface rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="h-14 px-5 border-b border-border flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-text">{editing ? 'Sửa Flash Deal' : 'Thêm Flash Deal'}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Chọn nhanh từ sản phẩm có sẵn</div>
            <select
              value={v.slug}
              onChange={(e) => handlePickProduct(e.target.value)}
              className={inputCls()}
            >
              <option value="">— Chọn sản phẩm —</option>
              {products.map((p) => (
                <option key={p.id} value={p.slug}>{p.name} ({formatVND(p.price)})</option>
              ))}
            </select>
            <div className="text-xs text-muted mt-1">Hoặc điền thủ công bên dưới</div>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Slug <span className="text-danger">*</span></div>
              <input value={v.slug} onChange={(e) => upd('slug', e.target.value)} className={inputCls(errors.slug)} placeholder="xe-may-dien-alpha-s" />
              {errors.slug && <div className="text-xs text-danger mt-1">{errors.slug}</div>}
            </label>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tên hiển thị <span className="text-danger">*</span></div>
              <input value={v.name} onChange={(e) => upd('name', e.target.value)} className={inputCls(errors.name)} placeholder="Alpha S" />
              {errors.name && <div className="text-xs text-danger mt-1">{errors.name}</div>}
            </label>
          </div>

          <label className="block">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-sm font-medium text-text">Ảnh (URL) <span className="text-danger">*</span></div>
              <label className="h-8 px-3 rounded-md border border-border bg-surface hover:bg-brand-50 hover:border-brand-500 cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium text-text">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
              </label>
            </div>
            <input value={v.image} onChange={(e) => upd('image', e.target.value)} onPaste={onPaste} className={inputCls(errors.image)} placeholder="https://... hoặc Ctrl+V ảnh" />
            {errors.image && <div className="text-xs text-danger mt-1">{errors.image}</div>}
          </label>

          {v.image && (
            <div className="aspect-[4/3] max-w-[200px] rounded-lg bg-slate-100 border border-border overflow-hidden" tabIndex={0} onPaste={onPaste}>
              <img src={v.image} alt="preview" className="w-full h-full object-contain" onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 3"><rect width="4" height="3" fill="%23e2e8f0"/></svg>'
              }} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Giá sale (đ) <span className="text-danger">*</span></div>
              <input type="number" value={v.price} onChange={(e) => upd('price', Number(e.target.value))} className={inputCls(errors.price)} />
              {errors.price && <div className="text-xs text-danger mt-1">{errors.price}</div>}
            </label>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Giá gốc (đ)</div>
              <input type="number" value={v.oldPrice} onChange={(e) => upd('oldPrice', Number(e.target.value))} className={inputCls()} />
              {discountPct > 0 && (
                <div className="text-xs text-accent-600 font-semibold mt-1">Giảm {discountPct}%</div>
              )}
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Đã bán</div>
              <input type="number" value={v.sold} onChange={(e) => upd('sold', Number(e.target.value))} className={inputCls(errors.sold)} />
              {errors.sold && <div className="text-xs text-danger mt-1">{errors.sold}</div>}
            </label>
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tổng số <span className="text-danger">*</span></div>
              <input type="number" value={v.stock} onChange={(e) => upd('stock', Number(e.target.value))} className={inputCls(errors.stock)} />
              {errors.stock && <div className="text-xs text-danger mt-1">{errors.stock}</div>}
            </label>
          </div>

          {v.stock > 0 && (
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-muted">Tiến độ bán</span>
                <span className="font-semibold text-text">{Math.round((v.sold / v.stock) * 100)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-orange-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-600 transition-all"
                  style={{ width: `${Math.min(100, (v.sold / v.stock) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-border flex items-center justify-end gap-3 shrink-0">
          <button onClick={onClose} className="h-10 px-4 rounded-md border border-border text-text hover:bg-surface text-sm font-medium">Hủy</button>
          <button onClick={handleSave} className="h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> {editing ? 'Lưu thay đổi' : 'Tạo deal'}
          </button>
        </div>
      </div>
    </div>
  )
}
